import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { PortalShell } from '@/components/portal/PortalShell'
import { PortalSection } from '@/components/portal/PortalSection'
import { PortalField } from '@/components/portal/PortalField'
import { PortalInput } from '@/components/portal/PortalInput'
import { PortalSelect } from '@/components/portal/PortalSelect'
import { PortalTextarea } from '@/components/portal/PortalTextarea'
import { Camera, Check, ChevronLeft, ChevronRight, CircleAlert, IdCard, Info, Loader2 } from 'lucide-react'
import { DraftActions } from '@/components/portal/DraftActions'
import { UploadTile } from '@/components/portal/UploadTile'
import { AgreementReview } from '@/components/portal/AgreementReview'
import type { WizardSaveState } from '@/hooks/useWizardAutosave'
import { useWizardAutosave } from '@/hooks/useWizardAutosave'
import { useOnboardingStatus } from '@/hooks/useOnboardingStatus'
import { useClientId } from '@/hooks/useClientId'
import { defaultWizardValues, type WizardFormValues } from '@/lib/wizard-draft'
import { useAuthStore } from '@/store/auth'
import { ANNUAL_INCOME_OPTIONS } from '@/lib/account-options'
import { INDONESIA_BANKS } from '@/constants/indonesia-banks'
import { UploadKycError, kycFormatMessageKey, uploadKycFile } from '@/lib/upload-kyc'
import { clientApi, ApiError } from '@/lib/api-client'
import { getSignupPhone } from '@/lib/signup-session'
import { wizardIssueKeys } from '@/lib/validators'
import { FIELD_MAX_LENGTH } from '@/lib/field-limits'
import { formatIdrWhole, parseIdrDigits } from '@/lib/utils'
import { ensureClientProfile, isValidClientId } from '@/lib/client-id'
import {
  AgreementSectionStepper,
  type AgreementWizardView,
} from '@/components/portal/AgreementSectionStepper'
import {
  AGREEMENT_SECTION_KEYS,
  agreementSectionId,
  agreementSectionForField,
  isAgreementSectionComplete,
  type AgreementSectionKey,
} from '@/lib/agreement-section-completion'

const setValueDirty = { shouldDirty: true, shouldValidate: false } as const

/** The six form sections, then the review step that holds the submit button. */
const WIZARD_VIEWS: readonly AgreementWizardView[] = [...AGREEMENT_SECTION_KEYS, 'review']

function sectionNumber(section: AgreementSectionKey): string {
  return String(AGREEMENT_SECTION_KEYS.indexOf(section) + 1).padStart(2, '0')
}

function SaveStatus({ state }: { state: WizardSaveState }) {
  const { t } = useTranslation()
  let content: React.ReactNode = null
  if (state === 'saving') {
    content = (
      <>
        <Loader2 className="size-3.5" aria-hidden="true" />
        {t('common.saving')}
      </>
    )
  } else if (state === 'saved') {
    content = (
      <>
        <Check className="size-3.5" aria-hidden="true" />
        {t('common.saved')}
      </>
    )
  } else if (state === 'error') {
    content = (
      <>
        <CircleAlert className="size-3.5" aria-hidden="true" />
        {t('common.saveFailed')}
      </>
    )
  } else if (state === 'idle') {
    content = t('agreement.autosaveIdle')
  }
  return (
    <p
      className={`prod-wizard-head__save${state === 'error' ? ' prod-wizard-head__save--error' : ''}`}
      aria-live="polite"
    >
      {content}
    </p>
  )
}

function YesNoField({
  label,
  value,
  onChange,
}: {
  label: string
  value: boolean
  onChange: (v: boolean) => void
}) {
  const { t } = useTranslation()
  return (
    <fieldset className="prod-field sm:col-span-2">
      <legend className="prod-field__label">{label}</legend>
      <div className="prod-radio-group">
        <label className="prod-radio">
          <input type="radio" name={label} checked={!value} onChange={() => onChange(false)} />
          {t('form.no')}
        </label>
        <label className="prod-radio">
          <input type="radio" name={label} checked={value} onChange={() => onChange(true)} />
          {t('form.yes')}
        </label>
      </div>
    </fieldset>
  )
}

function CurrencyField({
  label,
  name,
  value,
  onChange,
  onBlur,
  required,
  error,
}: {
  label: string
  name: string
  value: number
  onChange: (v: number) => void
  onBlur?: () => void
  required?: boolean
  error?: string
}) {
  const [focused, setFocused] = useState(false)
  const [display, setDisplay] = useState(() => formatIdrWhole(value))

  useEffect(() => {
    if (!focused) setDisplay(formatIdrWhole(value))
  }, [value, focused])

  return (
    <PortalField label={label} required={required} error={error}>
      <div className="prod-currency">
        <span className="prod-currency__prefix">IDR</span>
        <PortalInput
          name={name}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          required={required}
          value={display}
          onFocus={() => {
            setFocused(true)
            if (value <= 0) setDisplay('')
          }}
          onChange={(e) => {
            const parsed = parseIdrDigits(e.target.value)
            onChange(parsed)
            setDisplay(parsed > 0 ? formatIdrWhole(parsed) : '')
          }}
          onBlur={() => {
            setFocused(false)
            setDisplay(formatIdrWhole(value))
            onBlur?.()
          }}
        />
      </div>
    </PortalField>
  )
}

export function AgreementFormPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const authUser = useAuthStore((s) => s.user)
  const { clientId, resolving } = useClientId()

  const form = useForm<WizardFormValues>({
    defaultValues: {
      ...defaultWizardValues,
      email: authUser?.email ?? '',
      full_name:
        authUser?.name && authUser.name.trim().toLowerCase() !== authUser.email.trim().toLowerCase()
          ? authUser.name
          : '',
    },
    mode: 'onChange',
  })

  const { register, setValue, watch } = form
  const values = watch()
  const fieldIssues = useMemo(() => wizardIssueKeys(values), [values])
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const [submitAttempted, setSubmitAttempted] = useState(false)
  const [serverFieldErrors, setServerFieldErrors] = useState<Record<string, string>>({})
  const markTouched = (field: string) => setTouched((prev) => ({ ...prev, [field]: true }))
  const issueText = (field: string) => {
    if (serverFieldErrors[field]) return serverFieldErrors[field]
    return (submitAttempted || touched[field]) && fieldIssues[field]
      ? t(`validation.${fieldIssues[field]}`, { max: FIELD_MAX_LENGTH[field as keyof WizardFormValues] })
      : undefined
  }
  const [formLocked, setFormLocked] = useState(false)
  const statusQ = useOnboardingStatus(clientId)
  const draftLocked = useMemo(() => {
    if (formLocked) return true
    if (statusQ.data?.awaiting_approval) return true
    const st = statusQ.data?.status
    if (st && st !== 'DRAFT') return true
    return false
  }, [formLocked, statusQ.data])
  const { saveState, saveNow, prepareForNavigation } = useWizardAutosave(form, {
    enabled: !draftLocked,
    onLocked: () => setFormLocked(true),
  })
  const [activeSection, setActiveSection] = useState<AgreementSectionKey>('profile')
  // The review step is a seventh view beside the six section keys; lib keys stay untouched.
  const [reviewOpen, setReviewOpen] = useState(false)
  // Bumped on every navigation so re-selecting the current view still scrolls up and refocuses.
  const [navTick, setNavTick] = useState(0)
  const viewHeadingRef = useRef<HTMLHeadingElement>(null)
  const currentView: AgreementWizardView = reviewOpen ? 'review' : activeSection
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [docSelfieError, setDocSelfieError] = useState<string | null>(null)
  const [docIdentityError, setDocIdentityError] = useState<string | null>(null)
  const [docSelfieUploading, setDocSelfieUploading] = useState(false)
  const [docIdentityUploading, setDocIdentityUploading] = useState(false)
  const [docSelfieRetry, setDocSelfieRetry] = useState<File | null>(null)
  const [docIdentityRetry, setDocIdentityRetry] = useState<File | null>(null)
  const [docSelfiePreviewUrl, setDocSelfiePreviewUrl] = useState<string | null>(null)
  const [docIdentityPreviewUrl, setDocIdentityPreviewUrl] = useState<string | null>(null)
  const docSelfieInputId = useId()
  const docIdentityInputId = useId()
  const docSelfieId = watch('doc_selfie_id')
  const docIdentityId = watch('doc_identity_id')

  useEffect(() => {
    if (!docSelfieId || docSelfiePreviewUrl) return
    void clientApi
      .getDocumentPreviewUrl(docSelfieId)
      .then((preview) => setDocSelfiePreviewUrl(preview.preview_url))
      .catch(() => {})
  }, [docSelfieId, docSelfiePreviewUrl])

  useEffect(() => {
    if (!docIdentityId || docIdentityPreviewUrl) return
    void clientApi
      .getDocumentPreviewUrl(docIdentityId)
      .then((preview) => setDocIdentityPreviewUrl(preview.preview_url))
      .catch(() => {})
  }, [docIdentityId, docIdentityPreviewUrl])

  const completedSections = useMemo(() => {
    const map = {} as Record<AgreementSectionKey, boolean>
    for (const key of AGREEMENT_SECTION_KEYS) {
      map[key] = isAgreementSectionComplete(key, values)
    }
    return map
  }, [values])

  // After a step change has rendered: start the new view at the top and move focus to its heading.
  useEffect(() => {
    if (navTick === 0) return
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
    viewHeadingRef.current?.focus({ preventScroll: true })
  }, [navTick])

  useEffect(() => {
    if (authUser?.email) setValue('email', authUser.email)
    const name = form.getValues('full_name').trim()
    const email = (authUser?.email ?? form.getValues('email')).trim()
    if (name && email && name.toLowerCase() === email.toLowerCase()) {
      setValue('full_name', '', setValueDirty)
    }
  }, [authUser, form, setValue])

  useEffect(() => {
    const current = form.getValues('mobile_phone')?.trim()
    if (current) return
    const phone = getSignupPhone()
    if (phone) setValue('mobile_phone', phone, setValueDirty)
  }, [form, setValue])

  function uploadErrorMessage(err: unknown): string {
    if (err instanceof UploadKycError) {
      switch (err.code) {
        case 'NO_CLIENT_ID':
          return t('form.uploadNoClientId')
        case 'S3_NETWORK':
          return t('form.uploadNetworkFailed')
        case 'INVALID_FORMAT':
          return err.formatKey ? t(kycFormatMessageKey(err.formatKey)) : t('form.uploadFormat.unknown')
        case 'INVALID_SIZE':
          return t('form.uploadSize')
        case 'INVALID_DIMENSION':
          return t('form.uploadDimension')
        case 'S3_PUT_FAILED':
          return t('form.uploadNetworkFailed')
        case 'API':
        default:
          return err.message || t('form.uploadFailed')
      }
    }
    return err instanceof Error ? err.message : t('form.uploadFailed')
  }

  async function ensureClientIdForUpload(): Promise<boolean> {
    if (isValidClientId(clientId)) return true
    if (resolving) return false
    const resolved = await ensureClientProfile(authUser)
    return isValidClientId(resolved)
  }

  async function handleDocumentUpload(
    field: 'doc_selfie' | 'doc_identity',
    docType: 'RECENT_PHOTO' | 'IDENTITY_CARD',
    file: File,
  ) {
    const setError = field === 'doc_selfie' ? setDocSelfieError : setDocIdentityError
    const setUploading = field === 'doc_selfie' ? setDocSelfieUploading : setDocIdentityUploading
    const setRetry = field === 'doc_selfie' ? setDocSelfieRetry : setDocIdentityRetry
    setError(null)
    setUploading(true)
    try {
      const ready = await ensureClientIdForUpload()
      if (!ready) {
        setError(t('form.uploadNoClientId'))
        return
      }
      const uploaded = await uploadKycFile(docType, file)
      setValue(field, uploaded.filename, setValueDirty)
      const idField = field === 'doc_selfie' ? 'doc_selfie_id' : 'doc_identity_id'
      setValue(idField, uploaded.documentId, setValueDirty)
      setRetry(null)
      try {
        const preview = await clientApi.getDocumentPreviewUrl(uploaded.documentId)
        const url = preview.preview_url
        if (field === 'doc_selfie') setDocSelfiePreviewUrl(url)
        else setDocIdentityPreviewUrl(url)
      } catch {
        if (field === 'doc_selfie') setDocSelfiePreviewUrl(null)
        else setDocIdentityPreviewUrl(null)
      }
      try {
        await saveNow()
      } catch {
        setError(t('common.saveFailed'))
      }
    } catch (err) {
      setError(uploadErrorMessage(err))
      const retryable =
        err instanceof UploadKycError &&
        (err.code === 'S3_NETWORK' || err.code === 'S3_PUT_FAILED' || err.code === 'API')
      setRetry(retryable ? file : null)
    } finally {
      setUploading(false)
    }
  }

  function showView(view: AgreementWizardView) {
    if (view === 'review') {
      setReviewOpen(true)
    } else {
      setReviewOpen(false)
      setActiveSection(view)
    }
    setNavTick((tick) => tick + 1)
  }

  /** Show one section, scrolled to the top with its heading focused. handleSubmit routes through it. */
  function scrollToSection(section: AgreementSectionKey) {
    showView(section)
  }

  const viewIndex = WIZARD_VIEWS.indexOf(currentView)
  const previousView = viewIndex > 0 ? WIZARD_VIEWS[viewIndex - 1] : null
  const nextView = viewIndex < WIZARD_VIEWS.length - 1 ? WIZARD_VIEWS[viewIndex + 1] : null
  const completedCount = AGREEMENT_SECTION_KEYS.filter((key) => completedSections[key]).length

  async function handleSubmit() {
    setSubmitError(null)
    setServerFieldErrors({})
    setSubmitAttempted(true)
    const incomplete = AGREEMENT_SECTION_KEYS.find((key) => !isAgreementSectionComplete(key, form.getValues()))
    if (incomplete || Object.keys(wizardIssueKeys(form.getValues())).length > 0) {
      setSubmitError(t('form.sectionsIncomplete'))
      if (incomplete) scrollToSection(incomplete)
      return
    }
    try {
      await saveNow()
      await clientApi.submitDraft()
      prepareForNavigation()
      setFormLocked(true)
      if (clientId) {
        await queryClient.refetchQueries({ queryKey: ['client', 'status', clientId] })
      }
      navigate('/register/cdds')
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        const detail = err.detail
        if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
          const code = 'code' in detail ? String((detail as { code?: string }).code) : ''
          const fields =
            'fields' in detail && typeof (detail as { fields?: unknown }).fields === 'object'
              ? ((detail as { fields: Record<string, string> }).fields ?? {})
              : null
          if (code === 'invalid_draft' && fields) {
            setServerFieldErrors(fields)
            setTouched((prev) => ({
              ...prev,
              ...Object.fromEntries(Object.keys(fields).map((key) => [key, true])),
            }))
            if (fields.form) setSubmitError(fields.form)
            const firstField = Object.keys(fields).find((key) => key !== 'form')
            const section =
              (firstField && agreementSectionForField(firstField)) ||
              AGREEMENT_SECTION_KEYS.find((key) => !isAgreementSectionComplete(key, form.getValues())) ||
              'profile'
            scrollToSection(section)
            return
          }
        }
      }
      if (err instanceof ApiError && err.status === 409) {
        setFormLocked(true)
        return
      }
      setSubmitError(t('common.saveFailed'))
    }
  }

  return (
    <PortalShell>
      <div className="prod-page-header">
        <h1 className="prod-text-page-title prod-text-page-title--left">
          {t('agreement.formTitle')}
        </h1>
        <p className="prod-auth__subtitle">{t('agreement.subtitle')}</p>
      </div>

      <form
        className="prod-page-content prod-page-content--draft-actions prod-page-content--agreement"
        onSubmit={(e) => e.preventDefault()}
        onBlur={(e) => {
          const target = e.target as unknown as { name?: string; type?: string; value?: string; readOnly?: boolean }
          const name = target.name
          if (name) markTouched(name)
          if (
            name &&
            !target.readOnly &&
            (target.type === 'text' || target.type === 'textarea') &&
            target.value !== target.value?.trim()
          ) {
            setValue(name as keyof WizardFormValues, (target.value ?? '').trim(), setValueDirty)
          }
        }}
      >
        <AgreementSectionStepper
          activeView={currentView}
          completedSections={completedSections}
          onSelect={scrollToSection}
          onSelectReview={() => showView('review')}
        />
        <div className="prod-agreement-main">
        <div className="prod-wizard-head">
          <div className="prod-wizard-head__row">
            <p className="prod-wizard-head__count">
              {reviewOpen
                ? t('agreement.reviewProgress', { done: completedCount, total: AGREEMENT_SECTION_KEYS.length })
                : t('agreement.wizardStep', {
                    current: viewIndex + 1,
                    total: AGREEMENT_SECTION_KEYS.length,
                    section: t(`form.sectionsShort.${activeSection}`),
                  })}
            </p>
            {!draftLocked && !reviewOpen ? <SaveStatus state={saveState} /> : null}
          </div>
          <div className="prod-segments" aria-hidden="true">
            {AGREEMENT_SECTION_KEYS.map((key) => (
              <span
                key={key}
                className={[
                  'prod-segments__item',
                  completedSections[key] ? 'prod-segments__item--done' : '',
                  !completedSections[key] && key === currentView ? 'prod-segments__item--current' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              />
            ))}
          </div>
        </div>
        {draftLocked ? (
          <p className="prod-info-banner" role="status">
            <Info className="prod-info-banner__icon size-4" aria-hidden />
            <span>
              {t('form.underReview')} {t('fundingPage.waitingApprovalHelper')}
            </span>
          </p>
        ) : null}
        {submitError ? (
          <p className="prod-error prod-wizard-error" role="alert">
            {submitError}
          </p>
        ) : null}
        {reviewOpen ? (
          <AgreementReview
            values={values}
            completedSections={completedSections}
            onEdit={scrollToSection}
            locked={draftLocked}
            headingRef={viewHeadingRef}
          />
        ) : (
        <fieldset className="prod-agreement-fieldset" disabled={draftLocked}>
        {activeSection === 'profile' ? (
        <PortalSection
          title={t('form.sections.profile')}
          id={agreementSectionId('profile')}
          number={sectionNumber('profile')}
          headingRef={viewHeadingRef}
        >
          <PortalField label={t('wizard.fullName')} required fullWidth error={issueText('full_name')}>
            <PortalInput maxLength={FIELD_MAX_LENGTH.full_name} {...register('full_name')} />
          </PortalField>
          <PortalField label={t('auth.email')} required hint={t('form.emailFromAccount')}>
            <PortalInput readOnly type="email" className="prod-input--readonly" {...register('email')} />
          </PortalField>
          <PortalField label={t('wizard.birthPlace')} required error={issueText('birth_place')}>
            <PortalInput maxLength={FIELD_MAX_LENGTH.birth_place} {...register('birth_place')} />
          </PortalField>
          <PortalField label={t('wizard.birthDate')} required error={issueText('birth_date')}>
            <PortalInput type="date" {...register('birth_date')} />
          </PortalField>
          <PortalField label={t('form.nationality')} required>
            <PortalSelect {...register('nationality_code')}>
              <option value="ID">Indonesia</option>
            </PortalSelect>
          </PortalField>
          <PortalField label={t('wizard.identityType')} required>
            <PortalSelect {...register('identity_type')}>
              <option value="KTP">KTP</option>
              <option value="SIM">SIM</option>
              <option value="PASSPORT">Passport</option>
            </PortalSelect>
          </PortalField>
          <PortalField label={t('wizard.identityNumber')} required error={issueText('identity_number')}>
            <PortalInput maxLength={FIELD_MAX_LENGTH.identity_number} {...register('identity_number')} />
          </PortalField>
          <PortalField label={t('wizard.npwp')} error={issueText('npwp_number')}>
            <PortalInput maxLength={FIELD_MAX_LENGTH.npwp_number} {...register('npwp_number')} />
            <p className="prod-auth__subtitle prod-text-caption prod-mt-md">
              {t('form.npwpHint')}
            </p>
          </PortalField>
          <fieldset className="prod-field">
            <legend className="prod-field__label prod-field__label--required">{t('wizard.gender')}</legend>
            <div className="prod-radio-group">
              <label className="prod-radio">
                <input type="radio" value="LAKI_LAKI" {...register('gender')} /> {t('form.male')}
              </label>
              <label className="prod-radio">
                <input type="radio" value="PEREMPUAN" {...register('gender')} /> {t('form.female')}
              </label>
            </div>
          </fieldset>
          <PortalField label={t('wizard.maritalStatus')}>
            <PortalSelect {...register('marital_status')}>
              <option value="TIDAK_KAWIN">{t('form.single')}</option>
              <option value="KAWIN">{t('form.married')}</option>
              <option value="JANDA">{t('form.widowed')}</option>
              <option value="DUDA">{t('form.divorced')}</option>
            </PortalSelect>
          </PortalField>
          <PortalField label={t('form.spouseName')} error={issueText('spouse_name')}>
            <PortalInput maxLength={FIELD_MAX_LENGTH.spouse_name} {...register('spouse_name')} />
          </PortalField>
          <PortalField label={t('wizard.motherName')} required error={issueText('mother_maiden_name')}>
            <PortalInput maxLength={FIELD_MAX_LENGTH.mother_maiden_name} {...register('mother_maiden_name')} />
          </PortalField>
          <PortalField label={t('wizard.address')} required fullWidth error={issueText('address_street')}>
            <PortalTextarea maxLength={FIELD_MAX_LENGTH.address_street} {...register('address_street')} />
          </PortalField>
          <PortalField label={t('wizard.postalCode')} required error={issueText('home_postal_code')}>
            <PortalInput maxLength={FIELD_MAX_LENGTH.home_postal_code} {...register('home_postal_code')} />
          </PortalField>
          <PortalField label={t('form.homePhone')} error={issueText('home_phone')}>
            <PortalInput maxLength={FIELD_MAX_LENGTH.home_phone} {...register('home_phone')} />
          </PortalField>
          <PortalField label={t('auth.phone')} required>
            <PortalInput {...register('mobile_phone')} />
          </PortalField>
          <PortalField label={t('form.homeFax')} error={issueText('home_fax')}>
            <PortalInput maxLength={FIELD_MAX_LENGTH.home_fax} {...register('home_fax')} />
          </PortalField>
          <PortalField label={t('form.homeOwnership')} required>
            <PortalSelect {...register('home_ownership_status')}>
              <option value="PRIBADI">{t('form.owned')}</option>
              <option value="KELUARGA">{t('form.family')}</option>
              <option value="SEWA">{t('form.rent')}</option>
              <option value="LAINNYA">{t('form.other')}</option>
            </PortalSelect>
          </PortalField>
          <PortalField label={t('form.ownershipNotes')} fullWidth error={issueText('home_ownership_notes')}>
            <PortalTextarea maxLength={FIELD_MAX_LENGTH.home_ownership_notes} {...register('home_ownership_notes')} />
          </PortalField>
          <PortalField label={t('form.accountPurpose')} required>
            <PortalSelect {...register('account_purpose')}>
              <option value="LINDUNG_NILAI">{t('form.hedging')}</option>
              <option value="GAIN">{t('form.gain')}</option>
              <option value="SPEKULASI">{t('form.speculation')}</option>
              <option value="LAINNYA">{t('form.other')}</option>
            </PortalSelect>
          </PortalField>
          <PortalField label={t('form.purposeNotes')} fullWidth error={issueText('account_purpose_notes')}>
            <PortalTextarea maxLength={FIELD_MAX_LENGTH.account_purpose_notes} {...register('account_purpose_notes')} />
          </PortalField>
          <YesNoField
            label={t('form.priorTransactions')}
            value={watch('prior_transactions')}
            onChange={(v) => setValue('prior_transactions', v, setValueDirty)}
          />
          <YesNoField
            label={t('form.investmentExperience')}
            value={watch('investment_experience')}
            onChange={(v) => setValue('investment_experience', v, setValueDirty)}
          />
          <YesNoField
            label={t('form.familyInRegulator')}
            value={watch('family_in_regulator')}
            onChange={(v) => setValue('family_in_regulator', v, setValueDirty)}
          />
          <YesNoField
            label={t('form.declaredBankrupt')}
            value={watch('declared_bankrupt')}
            onChange={(v) => setValue('declared_bankrupt', v, setValueDirty)}
          />
        </PortalSection>
        ) : null}

        {activeSection === 'employment' ? (
        <PortalSection
          title={t('form.sections.employment')}
          id={agreementSectionId('employment')}
          number={sectionNumber('employment')}
          headingRef={viewHeadingRef}
        >
          <PortalField label={t('wizard.occupation')} required>
            <PortalSelect {...register('occupation')}>
              <option value="SWASTA">Swasta</option>
              <option value="WIRASWASTA">Wiraswasta</option>
              <option value="ASN">ASN</option>
              <option value="LAINNYA">{t('form.other')}</option>
            </PortalSelect>
          </PortalField>
          <PortalField label={t('form.occupationOther')} error={issueText('occupation_other')}>
            <PortalInput maxLength={FIELD_MAX_LENGTH.occupation_other} {...register('occupation_other')} />
          </PortalField>
          <PortalField label={t('wizard.companyName')} required error={issueText('company_name')}>
            <PortalInput maxLength={FIELD_MAX_LENGTH.company_name} {...register('company_name')} />
          </PortalField>
          <PortalField label={t('wizard.industry')} required error={issueText('industry_field')}>
            <PortalInput maxLength={FIELD_MAX_LENGTH.industry_field} {...register('industry_field')} />
          </PortalField>
          <PortalField label={t('wizard.position')} required error={issueText('position')}>
            <PortalInput maxLength={FIELD_MAX_LENGTH.position} {...register('position')} />
          </PortalField>
          <PortalField label={t('wizard.yearsAtCompany')} required>
            <PortalInput type="number" {...register('years_at_company', { valueAsNumber: true })} />
          </PortalField>
          <PortalField label={t('form.yearsPrevious')}>
            <PortalInput type="number" {...register('years_at_previous_company', { valueAsNumber: true })} />
          </PortalField>
          <PortalField label={t('form.officeAddress')} fullWidth error={issueText('office_address')}>
            <PortalTextarea maxLength={FIELD_MAX_LENGTH.office_address} {...register('office_address')} />
          </PortalField>
          <PortalField label={t('form.officePostal')} error={issueText('office_postal_code')}>
            <PortalInput maxLength={FIELD_MAX_LENGTH.office_postal_code} {...register('office_postal_code')} />
          </PortalField>
          <PortalField label={t('form.officePhone')} error={issueText('office_phone')}>
            <PortalInput maxLength={FIELD_MAX_LENGTH.office_phone} {...register('office_phone')} />
          </PortalField>
          <PortalField label={t('form.officeFax')} error={issueText('office_fax')}>
            <PortalInput maxLength={FIELD_MAX_LENGTH.office_fax} {...register('office_fax')} />
          </PortalField>
        </PortalSection>
        ) : null}

        {activeSection === 'wealth' ? (
        <PortalSection
          title={t('form.sections.wealth')}
          id={agreementSectionId('wealth')}
          number={sectionNumber('wealth')}
          headingRef={viewHeadingRef}
        >
          <PortalField label={t('wizard.annualIncome')} required>
            <PortalSelect {...register('annual_income')}>
              {ANNUAL_INCOME_OPTIONS.map((inc) => (
                <option key={inc} value={inc}>
                  {t(`wizard.annualIncomeOptions.${inc}`)}
                </option>
              ))}
            </PortalSelect>
          </PortalField>
          <CurrencyField
            label={t('form.njop')}
            name="njop_value"
            required
            value={watch('njop_value')}
            onChange={(v) => setValue('njop_value', v, setValueDirty)}
            onBlur={() => markTouched('njop_value')}
            error={issueText('njop_value')}
          />
          <PortalField label={t('form.homeLocation')} fullWidth error={issueText('home_location')}>
            <PortalTextarea maxLength={FIELD_MAX_LENGTH.home_location} {...register('home_location')} />
          </PortalField>
          <CurrencyField
            label={t('form.totalSavings')}
            name="total_savings"
            required
            value={watch('total_savings')}
            onChange={(v) => setValue('total_savings', v, setValueDirty)}
            onBlur={() => markTouched('total_savings')}
            error={issueText('total_savings')}
          />
          <CurrencyField
            label={t('form.totalAssets')}
            name="total_assets"
            required
            value={watch('total_assets')}
            onChange={(v) => setValue('total_assets', v, setValueDirty)}
            onBlur={() => markTouched('total_assets')}
            error={issueText('total_assets')}
          />
          <PortalField label={t('form.otherAssets')} fullWidth error={issueText('other_assets')}>
            <PortalInput maxLength={FIELD_MAX_LENGTH.other_assets} {...register('other_assets')} />
          </PortalField>
        </PortalSection>
        ) : null}

        {activeSection === 'bank' ? (
        <PortalSection
          title={t('form.sections.bank')}
          hint={t('form.bankHint')}
          id={agreementSectionId('bank')}
          number={sectionNumber('bank')}
          headingRef={viewHeadingRef}
        >
          <PortalField label={t('wizard.bankName')} required>
            <PortalSelect {...register('bank_name')}>
              <option value="">{t('form.selectBank')}</option>
              {INDONESIA_BANKS.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </PortalSelect>
          </PortalField>
          <PortalField label={t('wizard.bankBranch')} required error={issueText('bank_branch')}>
            <PortalInput maxLength={FIELD_MAX_LENGTH.bank_branch} {...register('bank_branch')} />
          </PortalField>
          <PortalField label={t('wizard.accountNumber')} required error={issueText('account_number')}>
            <PortalInput maxLength={FIELD_MAX_LENGTH.account_number} {...register('account_number')} />
          </PortalField>
          <PortalField
            label={t('form.accountHolder')}
            required
            error={issueText('account_holder')}
          >
            <PortalInput maxLength={FIELD_MAX_LENGTH.account_holder} {...register('account_holder')} />
          </PortalField>
          <PortalField label={t('form.bankPhone')} error={issueText('bank_phone')}>
            <PortalInput maxLength={FIELD_MAX_LENGTH.bank_phone} {...register('bank_phone')} />
          </PortalField>
          <PortalField label={t('wizard.accountTypeBank')} required>
            <PortalSelect {...register('bank_account_type')}>
              <option value="TABUNGAN">Tabungan</option>
              <option value="GIRO">Giro</option>
              <option value="LAINNYA">{t('form.other')}</option>
            </PortalSelect>
          </PortalField>
          <PortalField label={t('form.bankAddress')} fullWidth error={issueText('bank_address')}>
            <PortalTextarea maxLength={FIELD_MAX_LENGTH.bank_address} {...register('bank_address')} />
          </PortalField>
          <PortalField label={t('form.bankCity')} error={issueText('bank_city')}>
            <PortalInput maxLength={FIELD_MAX_LENGTH.bank_city} {...register('bank_city')} />
          </PortalField>
          <PortalField label={t('form.bankProvince')} error={issueText('bank_province')}>
            <PortalInput maxLength={FIELD_MAX_LENGTH.bank_province} {...register('bank_province')} />
          </PortalField>
          <PortalField label={t('form.bankSwift')} error={issueText('bank_swift')}>
            <PortalInput maxLength={FIELD_MAX_LENGTH.bank_swift} {...register('bank_swift')} />
          </PortalField>
          <PortalField label={t('form.bankIban')} error={issueText('bank_iban')}>
            <PortalInput maxLength={FIELD_MAX_LENGTH.bank_iban} {...register('bank_iban')} />
          </PortalField>
        </PortalSection>
        ) : null}

        {activeSection === 'emergency' ? (
        <PortalSection
          title={t('form.sections.emergency')}
          hint={t('form.emergencyHint')}
          id={agreementSectionId('emergency')}
          number={sectionNumber('emergency')}
          headingRef={viewHeadingRef}
        >
          <PortalField label={t('wizard.emergencyName')} required error={issueText('emergency_name')}>
            <PortalInput maxLength={FIELD_MAX_LENGTH.emergency_name} {...register('emergency_name')} />
          </PortalField>
          <PortalField label={t('wizard.address')} fullWidth error={issueText('emergency_address')}>
            <PortalTextarea maxLength={FIELD_MAX_LENGTH.emergency_address} {...register('emergency_address')} />
          </PortalField>
          <PortalField label={t('wizard.postalCode')} error={issueText('emergency_postal_code')}>
            <PortalInput maxLength={FIELD_MAX_LENGTH.emergency_postal_code} {...register('emergency_postal_code')} />
          </PortalField>
          <PortalField label={t('wizard.emergencyPhone')} required error={issueText('emergency_phone')}>
            <PortalInput maxLength={FIELD_MAX_LENGTH.emergency_phone} {...register('emergency_phone')} />
          </PortalField>
          <PortalField label={t('wizard.emergencyRelation')} required error={issueText('emergency_relation')}>
            <PortalInput maxLength={FIELD_MAX_LENGTH.emergency_relation} {...register('emergency_relation')} />
          </PortalField>
        </PortalSection>
        ) : null}

        {activeSection === 'documents' ? (
        <PortalSection
          title={t('form.sections.documents')}
          hint={t('form.documentsHint')}
          hintVariant="warn"
          id={agreementSectionId('documents')}
          number={sectionNumber('documents')}
          headingRef={viewHeadingRef}
        >
          <UploadTile
            inputId={docSelfieInputId}
            title={t('form.docSelfie')}
            required
            icon={<Camera className="size-7" />}
            fileName={watch('doc_selfie')}
            previewUrl={docSelfiePreviewUrl}
            uploading={docSelfieUploading}
            error={docSelfieError}
            onRetry={
              docSelfieRetry
                ? () => void handleDocumentUpload('doc_selfie', 'RECENT_PHOTO', docSelfieRetry)
                : undefined
            }
            locked={draftLocked}
            onFile={(file) => void handleDocumentUpload('doc_selfie', 'RECENT_PHOTO', file)}
          />
          <UploadTile
            inputId={docIdentityInputId}
            title={t('form.docIdentity')}
            required
            icon={<IdCard className="size-7" />}
            fileName={watch('doc_identity')}
            previewUrl={docIdentityPreviewUrl}
            uploading={docIdentityUploading}
            error={docIdentityError}
            onRetry={
              docIdentityRetry
                ? () => void handleDocumentUpload('doc_identity', 'IDENTITY_CARD', docIdentityRetry)
                : undefined
            }
            locked={draftLocked}
            onFile={(file) => void handleDocumentUpload('doc_identity', 'IDENTITY_CARD', file)}
          />
        </PortalSection>
        ) : null}

        </fieldset>
        )}

        {reviewOpen && !draftLocked ? (
          <DraftActions
            onSubmit={() => void handleSubmit()}
            saving={saveState === 'saving'}
            saveError={saveState === 'error'}
            leading={
              <button
                type="button"
                className="prod-btn prod-btn--outline prod-wizard-nav__back"
                onClick={() => showView('documents')}
              >
                <ChevronLeft className="size-4" aria-hidden="true" />
                {t('common.back')}
              </button>
            }
          />
        ) : (
          <div className="prod-draft-bar prod-wizard-nav">
            {previousView ? (
              <button
                type="button"
                className="prod-btn prod-btn--outline prod-wizard-nav__back"
                onClick={() => showView(previousView)}
              >
                <ChevronLeft className="size-4" aria-hidden="true" />
                {t('common.back')}
              </button>
            ) : null}
            {nextView ? (
              <button
                type="button"
                className="prod-btn prod-btn--gold prod-wizard-nav__next"
                onClick={() => showView(nextView)}
              >
                {nextView === 'review' ? t('agreement.toReview') : t('agreement.next')}
                <ChevronRight className="size-4" aria-hidden="true" />
              </button>
            ) : null}
          </div>
        )}
        </div>
      </form>
    </PortalShell>
  )
}
